import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  DispatchOptions,
  DispatchShipmentRequest,
  DispatchJntShipmentRequest,
  ShipmentResponse,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateOrderShipment } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";
import { resolveBostaErrorMessage } from "@/modules/settings/lib/bostaErrors";

type OrderShipmentParams = {
  storeSlug: string;
  orderId: string;
};

type ShipmentMutationCallbacks = {
  onError?: (error: unknown) => void;
};

export function useOrderShipmentQuery({
  storeSlug,
  orderId,
}: OrderShipmentParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.orderShipment(storeSlug, orderId),
    queryFn: () =>
      apiJsonClient<ShipmentResponse>(endpoints.seller.orderShipment(orderId)),
    enabled: storeSlug.length > 0 && orderId.length > 0,
  });

  return {
    shipment: query.data?.shipment ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

type DispatchOptionsParams = OrderShipmentParams & {
  enabled?: boolean;
};

export function useDispatchOptionsQuery({
  storeSlug,
  orderId,
  enabled = true,
}: DispatchOptionsParams) {
  const query = useQuery({
    queryKey: queryKeys.seller.orderShipmentOptions(storeSlug, orderId),
    queryFn: () =>
      apiJsonClient<DispatchOptions>(
        endpoints.seller.orderShipmentOptions(orderId),
      ),
    enabled: enabled && storeSlug.length > 0 && orderId.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  return {
    options: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

type DispatchInput = OrderShipmentParams & DispatchShipmentRequest;

export function useDispatchShipmentMutation(
  callbacks?: ShipmentMutationCallbacks,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DispatchInput) => {
      const { storeSlug: _storeSlug, orderId, ...body } = input;
      return apiJsonClient<ShipmentResponse>(
        endpoints.seller.orderShipment(orderId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateOrderShipment(
        queryClient,
        variables.storeSlug,
        variables.orderId,
      );
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveBostaErrorMessage(mutation.error).message
      : null,
  };
}

type DispatchJntInput = OrderShipmentParams & DispatchJntShipmentRequest;

export function useDispatchJntShipmentMutation(
  callbacks?: ShipmentMutationCallbacks,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: DispatchJntInput) => {
      const { storeSlug: _storeSlug, orderId, ...body } = input;
      return apiJsonClient<ShipmentResponse>(
        endpoints.seller.orderShipmentJnt(orderId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
    },
    onSuccess: async (_data, variables) => {
      await invalidateOrderShipment(
        queryClient,
        variables.storeSlug,
        variables.orderId,
      );
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveBostaErrorMessage(mutation.error).message
      : null,
  };
}

export function useCancelShipmentMutation(
  callbacks?: ShipmentMutationCallbacks,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: OrderShipmentParams) =>
      apiJsonClient<ShipmentResponse>(
        endpoints.seller.orderShipment(input.orderId),
        { method: "DELETE" },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateOrderShipment(
        queryClient,
        variables.storeSlug,
        variables.orderId,
      );
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveBostaErrorMessage(mutation.error).message
      : null,
  };
}

// On-demand pull from Bosta for missed webhooks — the backend re-runs the
// fetched state through the same apply path as a webhook event.
export function useRefreshShipmentMutation(
  callbacks?: ShipmentMutationCallbacks,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: OrderShipmentParams) =>
      apiJsonClient<ShipmentResponse>(
        endpoints.seller.orderShipmentRefresh(input.orderId),
        { method: "POST" },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateOrderShipment(
        queryClient,
        variables.storeSlug,
        variables.orderId,
      );
    },
    onError: (error) => callbacks?.onError?.(error),
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    errorMessage: mutation.error
      ? resolveBostaErrorMessage(mutation.error).message
      : null,
  };
}
